import React, { useState, useEffect, useCallback } from 'react';
import PartnerLayout from '@/components/layouts/PartnerLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { usePartner, API } from '@/contexts/PartnerContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowDownToLine, Plus, Loader2, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

const STATUS_STYLE = {
  pending:  'text-amber-500 bg-amber-500/10 border-amber-500/20',
  approved: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
  paid:     'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
  rejected: 'text-red-500 bg-red-500/10 border-red-500/20',
};
const STATUS_ICON = { pending: Clock, approved: CheckCircle2, paid: CheckCircle2, rejected: XCircle };

const NETWORKS = ['USDT-TRC20', 'USDT-BEP20', 'USDT-ERC20', 'BTC', 'ETH'];

export default function PartnerWithdrawals() {
  const { headers } = usePartner();
  const [withdrawals, setWithdrawals] = useState([]);
  const [pendingBalance, setPendingBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ amount: '', wallet_address: '', network: 'USDT-TRC20', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/partner/withdrawals`, { headers });
      setWithdrawals(data.data || []);
      setPendingBalance(data.pending_commission || 0);
    } catch { toast.error('Failed to load withdrawals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRequest = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.wallet_address) { toast.error('Enter wallet address'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/partner/withdrawals`, { ...form, amount }, { headers });
      setWithdrawals(prev => [data, ...prev]);
      setPendingBalance(b => b - amount);
      toast.success('Withdrawal request submitted');
      setModalOpen(false);
      setForm({ amount: '', wallet_address: '', network: 'USDT-TRC20', notes: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally { setSaving(false); }
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <PartnerLayout title="Withdrawals">
      <div className="space-y-5">
        {/* Balance + CTA */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Available to Withdraw</p>
              <p className="text-3xl font-bold text-foreground" data-testid="available-balance">${pendingBalance.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground mt-1">Minimum withdrawal: $10.00 · Paid in USDT</p>
            </div>
            <Button onClick={() => setModalOpen(true)} disabled={pendingBalance < 10} className="gap-2" data-testid="request-withdrawal-btn">
              <ArrowDownToLine className="h-4 w-4" /> Request Withdrawal
            </Button>
          </CardContent>
        </Card>

        {/* History */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : withdrawals.length === 0 ? (
          <Card><CardContent className="py-16 text-center">
            <ArrowDownToLine className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-muted-foreground">No withdrawal requests yet</p>
          </CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="withdrawals-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Date</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Amount</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Network</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Wallet</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {withdrawals.map((w, i) => {
                      const Icon = STATUS_ICON[w.status] || Clock;
                      return (
                        <tr key={w.id || i} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(w.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-foreground">${w.amount.toFixed(2)}</td>
                          <td className="px-4 py-3 text-[13px] text-muted-foreground">{w.network}</td>
                          <td className="px-4 py-3 text-[13px] font-mono text-muted-foreground max-w-[180px] truncate">{w.wallet_address}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border ${STATUS_STYLE[w.status] || ''}`}>
                              <Icon className="h-3 w-3" /> {w.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{w.admin_note || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Request Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-sm" data-testid="withdrawal-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowDownToLine className="h-4 w-4" /> Request Withdrawal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="rounded-xl border border-border bg-muted/40 p-3 flex justify-between text-sm">
              <span className="text-muted-foreground">Available</span>
              <span className="font-bold text-foreground">${pendingBalance.toFixed(2)}</span>
            </div>
            <div>
              <Label className="text-xs">Amount (USD) *</Label>
              <Input type="number" min="10" step="0.01" value={form.amount} onChange={f('amount')}
                placeholder="Min $10.00" data-testid="wd-amount" />
            </div>
            <div>
              <Label className="text-xs">Network *</Label>
              <select value={form.network} onChange={f('network')} data-testid="wd-network"
                className="w-full h-10 px-3 text-sm bg-background border border-input rounded-lg focus:outline-none">
                {NETWORKS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Wallet Address *</Label>
              <Input value={form.wallet_address} onChange={f('wallet_address')}
                placeholder="Enter wallet address" data-testid="wd-address" />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={form.notes} onChange={f('notes')} placeholder="Any notes for admin" />
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Withdrawals are processed within 2-5 business days after admin approval.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleRequest} disabled={saving} data-testid="confirm-withdrawal-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PartnerLayout>
  );
}
